import { Injectable, signal, Signal, WritableSignal, inject } from '@angular/core';

import { forkJoin } from 'rxjs';

import { PortfolioApiService } from '@app/common/services/portfolio-api.service';
import { PortfolioCategory, PortfolioItem, PortfolioPagination } from '@app/types/portfolio.types';

@Injectable({
  providedIn: 'root'
})
export class PortfolioDataService {
  private portfolioApiService = inject(PortfolioApiService);

  private _portfolioCategories: WritableSignal<PortfolioCategory[]> = signal([]);
  private _portfolioItems: WritableSignal<PortfolioItem[]> = signal([]);
  private _portfolioPagination: WritableSignal<PortfolioPagination> = signal({ currentPage: 1, totalPages: 0 });

  private _allPortfolioCategories: PortfolioCategory[] = [];
  private _allPortfolioItems: PortfolioItem[] = [];
  private mainCategoryId: string;
  private readonly ITEMS_PER_PAGE = 6;

  init(): Promise<void> {
    return new Promise((resolve) => {
      forkJoin({
        categories: this.portfolioApiService.getPortfolioCategories(),
        items: this.portfolioApiService.getPortfolioItems()
      }).subscribe((data) => {
        this._allPortfolioCategories = data.categories.map((c) => ({ ...c, selected: false }));
        this._allPortfolioItems = data.items;
        this.mainCategoryId = this._allPortfolioCategories.find((c) => c.position === 1)._id;

        this.removeUnusedPortfolioCategories();
        this.selectCategory(this.mainCategoryId);
        resolve();
      });
    });
  }

  getPortfolioCategories(): Signal<PortfolioCategory[]> {
    return this._portfolioCategories.asReadonly();
  }

  getAllPortfolioItems(): PortfolioItem[] {
    return [...this._allPortfolioItems];
  }

  getFilteredPortfolioItems(): Signal<PortfolioItem[]> {
    return this._portfolioItems.asReadonly();
  }

  getRandomPortfolioItems(numberOfItems: number): PortfolioItem[] {
    const allPortfolioItems = this.getAllPortfolioItems();
    this.shuffle(allPortfolioItems);
    return allPortfolioItems.slice(0, numberOfItems);
  }

  getPortfolioPagination(): Signal<PortfolioPagination> {
    return this._portfolioPagination.asReadonly();
  }

  getPortfolioItemBySlug(slug: string): PortfolioItem {
    return this.getAllPortfolioItems().find((i) => i.slug === slug) || null;
  }

  setPage(pageNumber: number): void {
    this._portfolioPagination.set({ currentPage: pageNumber, totalPages: this._portfolioPagination().totalPages });
    this.filterAndPaginateItems();
  }

  selectCategory(categoryId: string): void {
    this._allPortfolioCategories.forEach((c) => (c.selected = c._id === categoryId));
    this._portfolioCategories.set(this._allPortfolioCategories);
    this.filterAndPaginateItems(true);
  }

  private removeUnusedPortfolioCategories(): void {
    const portfolioItemsCategories = this.getAllPortfolioItems().map((item) => item.categoryId);
    this._allPortfolioCategories = this._allPortfolioCategories.filter((c) => c._id === this.mainCategoryId || portfolioItemsCategories.includes(c._id));
  }

  private filterAndPaginateItems(resetPage = false): void {
    const selectedCategoryId = this._portfolioCategories().find((c) => c.selected)._id;
    const actualPage = resetPage ? 1 : this._portfolioPagination().currentPage;
    const starElementIndex = (actualPage - 1) * this.ITEMS_PER_PAGE;

    const allPortfolioItems = this.getAllPortfolioItems();
    const filteredPortfolioItems = selectedCategoryId === this.mainCategoryId ? allPortfolioItems : allPortfolioItems.filter((item) => item.categoryId === selectedCategoryId);
    const paginatedItems = filteredPortfolioItems.slice(starElementIndex, starElementIndex + this.ITEMS_PER_PAGE);

    this._portfolioItems.set(paginatedItems);
    this._portfolioPagination.set({ currentPage: actualPage, totalPages: Math.ceil(filteredPortfolioItems.length / this.ITEMS_PER_PAGE) });
  }

  private shuffle(array: PortfolioItem[]) {
    let currentIndex = array.length;
    while (currentIndex != 0) {
      const randomIndex = Math.floor(Math.random() * currentIndex);
      currentIndex--;
      [array[currentIndex], array[randomIndex]] = [array[randomIndex], array[currentIndex]];
    }
  }
}
